import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Grid, 
  Box,
  Paper,
  List,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArticleIcon from '@mui/icons-material/Article';
import GavelIcon from '@mui/icons-material/Gavel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CircularProgress from '@mui/material/CircularProgress';
import { documentService, getStrapiMediaUrl } from '../services/api';

const Covenants = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);

  // Static fallback documents in case API fails
  const fallbackDocuments = [
    {
      title: "Original Covenants",
      description: "The foundational covenants established for Brynleigh Estates",
      icon: <GavelIcon color="primary" />,
      fileName: "Brynleigh_Estates_Covenant_Revised_06192013.pdf",
      viewPath: "/document/covenant"
    },
    {
      title: "HOA Bylaws",
      description: "The governing bylaws for the Homeowners Association",
      icon: <DescriptionIcon color="primary" />,
      fileName: "Brynleigh_Estates_ByLaws_Revised_06192013.pdf",
      viewPath: "/document/bylaws"
    },
    {
      title: "Articles of Incorporation",
      description: "Legal articles of incorporation for the Homeowners Association",
      icon: <ArticleIcon color="primary" />,
      fileName: "Brynleigh_Estates_Articles_Incorporation.pdf",
      viewPath: "/document/articles"
    },
    {
      title: "Annual Meeting Minutes",
      description: "Minutes from the 2024 Annual HOA meeting",
      icon: <ArticleIcon color="primary" />,
      fileName: "Brynleigh_Estates_2024_Annual_Meeting_Minutes.pdf",
      viewPath: "/document/minutes"
    }
  ];

  // Load documents from the API on component mount
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const response = await documentService.getAll();
        
        if (response && response.data) {
          // Process the API response to match our format
          const apiDocuments = response.data.map(doc => {
            // Choose icon based on category
            let icon;
            switch(doc.attributes.category) {
              case 'covenants':
                icon = <GavelIcon color="primary" />;
                break;
              case 'bylaws':
                icon = <DescriptionIcon color="primary" />;
                break;
              case 'meeting_minutes':
                icon = <ArticleIcon color="primary" />;
                break;
              default:
                icon = <DescriptionIcon color="primary" />;
            }
            
            // Get document URL
            const documentUrl = doc.attributes.document?.data 
              ? getStrapiMediaUrl(doc.attributes.document.data.attributes.url)
              : null;
              
            return {
              id: doc.id,
              title: doc.attributes.title,
              description: doc.attributes.description || '',
              icon: icon,
              documentUrl: documentUrl,
              viewPath: `/document/${doc.id}`,
              category: doc.attributes.category,
              isPublic: doc.attributes.isPublic,
              requiresLogin: doc.attributes.requiresLogin
            };
          });
          
          if (apiDocuments.length > 0) {
            setDocuments(apiDocuments);
          } else {
            setDocuments(fallbackDocuments);
          }
        } else {
          setDocuments(fallbackDocuments);
        }
      } catch (err) {
        console.error('Error fetching documents:', err);
        setDocuments(fallbackDocuments);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const faqs = [
    {
      question: "How do I submit a request for architectural approval?",
      answer: "Submit all requests to the Architectural Review Committee (ARC) using the form available in the Documents section. Include detailed plans, material specifications, and the location of your proposed changes. Allow up to 30 days for review."
    },
    {
      question: "When are HOA dues collected?",
      answer: "HOA dues are collected annually and are due by January 31st of each year. Late payments may be subject to penalties as outlined in the bylaws."
    },
    {
      question: "How do I report a covenant violation?",
      answer: "You can report violations by contacting the HOA board via email. Please provide your name, address, and details about the violation. All reports are kept confidential."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Covenants & Documents | Brynleigh Estates HOA</title>
        <meta name="description" content="Access important HOA documents, covenants, bylaws, and community guidelines for the Brynleigh Estates community in Chelsea, Alabama." />
        <link rel="canonical" href="https://brynleighestates.com/covenants" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/covenants" />
        <meta property="og:title" content="Covenants & Documents | Brynleigh Estates HOA" />
        <meta property="og:description" content="Access important HOA documents, covenants, bylaws, and community guidelines for the Brynleigh Estates community in Chelsea, Alabama." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Covenants & Documents | Brynleigh Estates HOA" />
        <meta name="twitter:description" content="Access important HOA documents, covenants, bylaws, and community guidelines for Brynleigh Estates." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>
      
      <Hero 
        imageUrl="/images/hills-bg.jpeg"
        title="Covenants & Documents"
        subtitle="Important information for homeowners"
      />
      
      <Container maxWidth="lg">
        <Box sx={{ my: 6 }}>
          <Alert severity="info" sx={{ mb: 4 }}>
            Please note that all homeowners are subject to the covenants, conditions, and restrictions. Familiarizing yourself with these documents helps maintain our community standards.
          </Alert>
          
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              mb: 4, 
              borderRadius: 2, 
              border: '2px solid', 
              borderColor: 'primary.main',
              bgcolor: 'rgba(46, 125, 50, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" color="primary.dark">
                2026 Proposed Covenant Changes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review the proposed amendments to Article I and the Preamble side-by-side with the original text.
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              color="primary"
              component={RouterLink}
              to="/covenant-comparison"
              startIcon={<DescriptionIcon />}
            >
              View Comparison
            </Button>
          </Paper>
          
          {error && (
            <Alert severity="warning" sx={{ mb: 4 }}>
              {error}
            </Alert>
          )}
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Typography variant="h4" gutterBottom fontWeight="medium" component="h1">
                Community Documents
              </Typography>
              
              <Typography variant="body1" paragraph>
                The following documents outline the guidelines, rules, and regulations that govern our community. These covenants are designed to protect property values and maintain the aesthetic quality of Brynleigh Estates.
              </Typography>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {documents.map((doc, index) => (
                    <Paper 
                      key={doc.id || index} 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        mb: 2, 
                        borderRadius: 2, 
                        border: '1px solid', 
                        borderColor: 'divider',
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: 1
                        }
                      }}
                    >
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 50, height: 50 }}>
                            {doc.icon}
                          </Box>
                        </Grid>
                        <Grid item xs>
                          <Typography variant="h6">{doc.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {doc.description}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button 
                              variant="contained" 
                              size="small"
                              startIcon={<VisibilityIcon />}
                              component={RouterLink}
                              to={doc.viewPath}
                            >
                              View
                            </Button>
                            {doc.documentUrl && (
                              <Button 
                                variant="outlined" 
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                href={doc.documentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Download
                              </Button>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </List>
              )}
              
              <Box sx={{ mt: 6 }}>
                <Typography variant="h5" gutterBottom component="h2">
                  Frequently Asked Questions
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                {faqs.map((faq, index) => (
                  <Accordion key={index} sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight="medium">{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography>{faq.answer}</Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 4 }}>
                <Typography variant="h5" gutterBottom fontWeight="medium" component="h2">
                  Need Help?
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <HelpOutlineIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    If you have questions about covenants or need assistance with document interpretation, please contact the HOA board.
                  </Typography>
                </Box>
                <Button 
                  variant="contained" 
                  fullWidth
                  href="mailto:board@brynleighestates.com"
                >
                  Contact the Board
                </Button>
              </Paper>
              
              <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h5" gutterBottom fontWeight="medium" component="h2">
                  Document Request
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" paragraph>
                  Can't find a specific document? Request additional documents from our archives by contacting the HOA secretary.
                </Typography>
                <Typography variant="body2" paragraph>
                  Please allow 3-5 business days for processing document requests.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default Covenants; 